import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Stack } from "@mui/material";
import type { Organization, Patient } from "fhir/r5";
import { useLocation } from "react-router-dom";
import {
  getEcommerceAudienceNoun,
  parseEcommerceListSearch,
  serializeEcommerceListSearch,
  useEcommercePatientsList,
  useEcommercePeopleList,
  type EcommerceListAudience,
  type EcommerceWhatsAppTarget,
  type PatientOpFilter,
  type UseEcommerceOrganizationPersonsResult,
  type UseEcommerceWhatsAppEndpointResult,
} from "src-next/modules/ecommerce";
import EcommercePatientsListSkeleton from "../components/EcommercePatientsListSkeleton";
import EcommercePatientQuickStats from "../components/EcommercePatientQuickStats";
import { EcommerceListControls } from "../components/EcommerceListControls";
import { EcommerceListBody } from "../components/EcommerceListBody";

interface EcommercePatientsListSectionProps {
  organization: Organization;
  onOpenPage: (
    patient: Patient,
    transactionId?: string,
    listSearch?: string,
  ) => void;
  listRevision?: number;
  organizationPersons: UseEcommerceOrganizationPersonsResult;
  whatsAppChannel: UseEcommerceWhatsAppEndpointResult;
  onSendWhatsApp: (
    input: Patient[] | EcommerceWhatsAppTarget[],
    options?: { onSent?: () => void },
  ) => void;
}

export const EcommercePatientsListSection = memo(
  function EcommercePatientsListSection({
    organization,
    onOpenPage,
    listRevision = 0,
    organizationPersons: orgPersons,
    whatsAppChannel,
    onSendWhatsApp,
  }: EcommercePatientsListSectionProps) {
    const location = useLocation();
    const initialListState = useMemo(
      () => parseEcommerceListSearch(location.search),
      // Hidrata só no mount. Não gravar query a cada clique — no hash router
      // isso remonta a lista e desabilita/reseta os filtros.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );
    const [page, setPage] = useState(initialListState.page);
    const [audience, setAudience] = useState<EcommerceListAudience>(
      initialListState.audience,
    );
    const list = useEcommercePatientsList(organization, listRevision, {
      searchTerm: initialListState.searchTerm,
      opFilter: initialListState.opFilter,
    });
    const people = useEcommercePeopleList(
      audience,
      list.patients,
      orgPersons.persons,
      list.searchTerm,
    );

    const isPeopleView = audience !== "transactions";
    const listSearch = serializeEcommerceListSearch({
      searchTerm: list.searchTerm,
      opFilter: list.opFilter,
      audience,
      page,
    });

    const skipNextPaginationResetRef = useRef(true);
    useEffect(() => {
      if (skipNextPaginationResetRef.current) {
        skipNextPaginationResetRef.current = false;
        return;
      }
      setPage(0);
    }, [list.searchTerm, list.opFilter, audience]);

    useEffect(() => {
      if (audience !== "registered_clients") return;
      orgPersons.refetch();
    }, [audience, orgPersons.refetch]);

    const handlePipelineFilter = useCallback(
      (filter: PatientOpFilter) => {
        setAudience("transactions");
        list.setOpFilter(filter);
        people.clearSelection();
      },
      [list, people],
    );

    const handleAudienceChange = useCallback(
      (next: EcommerceListAudience) => {
        setAudience(next);
        if (next !== "transactions") list.setOpFilter("all");
        list.clearSelection();
        people.clearSelection();
      },
      [list, people],
    );

    const visibleCount = isPeopleView
      ? people.filteredRows.length
      : list.filteredRows.length;
    const selectedCount = isPeopleView
      ? people.selectedKeys.length
      : list.selectedKeys.length;
    const selectedFilteredCount = isPeopleView
      ? people.selectedFilteredCount
      : list.selectedFilteredCount;

    if (list.isInitialLoading) {
      return <EcommercePatientsListSkeleton />;
    }

    return (
      <Stack spacing={3}>
        {!list.cnpj && (
          <Alert severity="warning">
            O CNPJ da organização não foi informado.
          </Alert>
        )}
        {list.error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={list.fetchPatients}>
                Tentar novamente
              </Button>
            }
          >
            Não foi possível carregar os pacientes com consentimento.
          </Alert>
        )}
        {orgPersons.error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={orgPersons.refetch}>
                Tentar novamente
              </Button>
            }
          >
            Não foi possível carregar os clientes cadastrados.
          </Alert>
        )}
        {list.listRefreshing && (
          <Alert severity="info" sx={{ py: 0.5 }}>
            Atualizando lista de pacientes…
          </Alert>
        )}

        <EcommercePatientQuickStats
          totalCount={list.transactionRows.length}
          docsPendingCount={list.docsPendingCount}
          docsRejectedCount={list.docsRejectedCount}
          docsToApproveCount={list.docsToApproveCount}
          awaitingReleaseCount={list.awaitingReleaseCount}
          docsReleasedCount={list.docsReleasedCount}
          purchaseCompletedCount={list.purchaseCompletedCount}
          activeFilter={audience === "transactions" ? list.opFilter : null}
          statsReady={list.resourcesPreloaded}
          onFilterChange={handlePipelineFilter}
          audience={audience}
          consentPatientCount={list.patients.length}
          registeredClientCount={orgPersons.persons.length}
          registeredClientsReady={
            orgPersons.countsReady || orgPersons.hasFetchedOnce
          }
          onAudienceChange={handleAudienceChange}
        />

        <EcommerceListControls
          searchLabel={isPeopleView ? "Buscar" : "Buscar paciente"}
          searchPlaceholder={
            isPeopleView ? "Nome ou CPF" : "Nome, e-mail ou CPF"
          }
          searchTerm={list.searchTerm}
          onSearchChange={list.setSearchTerm}
          filterHint={
            audience === "transactions" &&
            list.activeFilterOption &&
            list.opFilter !== "all" &&
            list.resourcesPreloaded ? (
              <Alert severity="info" sx={{ py: 0.5 }}>
                {list.activeFilterOption.description}
                {!list.dispensesPreloaded
                  ? " Contagem de liberações completa ao abrir os pacientes nesta sessão."
                  : null}
              </Alert>
            ) : null
          }
          visibleCount={visibleCount}
          selectedCount={selectedCount}
          selectedFilteredCount={selectedFilteredCount}
          noun={getEcommerceAudienceNoun(audience, visibleCount)}
          whatsAppDisabled={selectedCount === 0 || whatsAppChannel.isLoading}
          onToggleAll={
            isPeopleView ? people.handleToggleAll : list.handleToggleAll
          }
          onSendWhatsApp={() =>
            onSendWhatsApp(
              isPeopleView ? people.selectedTargets : list.selectedPatients,
              {
                onSent: isPeopleView
                  ? people.clearSelection
                  : list.clearSelection,
              },
            )
          }
        />

        <EcommerceListBody
          audience={audience}
          orgPersons={orgPersons}
          consentPatientCount={list.patients.length}
          peopleRows={people.filteredRows}
          peopleSelectedKeys={people.selectedKeys}
          onTogglePeopleRow={people.handleToggleRow}
          transactionRows={list.filteredRows}
          transactionSelectedKeys={list.selectedKeys}
          getTransactionRowKey={list.getRowKey}
          onToggleTransactionRow={list.handleTogglePatient}
          showStatusColumn={list.resourcesPreloaded}
          page={page}
          onPageChange={setPage}
          onManagePeople={(patient) => onOpenPage(patient, undefined, listSearch)}
          onManageTransaction={(patient, transactionId) =>
            onOpenPage(patient, transactionId, listSearch)
          }
          onSendClientWhatsApp={(row) =>
            onSendWhatsApp([{ patient: row.patient, person: row.person }])
          }
          whatsAppDisabled={whatsAppChannel.isLoading}
        />
      </Stack>
    );
  },
);
